import {
    DisconnectOutlined,
    MailOutlined,
    MoonOutlined,
    RightOutlined,
    SyncOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { App, Button, Flex, Popconfirm } from "antd";
import { useState } from "react";
import { postDevFrontDebug } from "tm-shared/api";
import { deepParseJson } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useSW } from "@/hooks/app/useSW";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import {
    StyledInfoBlock,
    StyledInfoTitle,
    StyledProfileCard,
} from "@/pages/Profile/styles";
import { PrimaryText, SecondaryText } from "@/shared/ui";
import {
    updateManualOfflineStatus,
    updateOfflineStatus,
} from "@/store/slices/offline/offline.slice";
import { toggleTheme } from "@/store/slices/theme/theme.slice";
import { isOnline } from "@/utils/network/isOnline";

import {
    StyledIcon,
    StyledLinkAccount,
    StyledSwitch,
    StyledTwoColumnsGrid,
} from "./Styled";

const storeResetWarning =
    "Після оновлення поточний прогрес у додатку буде скинуто.";

export const UserSettingsBlock = () => {
    const { notification } = App.useApp();
    const type = useAppSelector((state) => state.theme.type);
    const offlineMode = useOfflineModeSelector();

    const [isSendingState, setIsSendingState] = useState(false);
    const [offlineLoading, setOfflineLoading] = useState(false);

    const dispatch = useAppDispatch();

    const handleThemeChange = () => {
        dispatch(toggleTheme());
    };

    const handleNetworkChange = async () => {
        if (offlineMode) {
            setOfflineLoading(true);
            const online = await isOnline(5000);
            setOfflineLoading(false);

            if (!online) {
                notification.warning({
                    title: "Перейти в онлайн наразі неможливо.",
                    description:
                        "Немає підключення до інтернету або з’єднання занадто слабке.",
                });
                return;
            }
        }

        dispatch(dispatch(updateOfflineStatus(!offlineMode)));
        dispatch(dispatch(updateManualOfflineStatus(!offlineMode)));
    };

    const handleSendState = async () => {
        setIsSendingState(true);
        const stringifiedState = localStorage.getItem("persist:root");
        const debugInfo = deepParseJson(stringifiedState) as Record<
            string,
            unknown
        >;

        const user = debugInfo?.user as { id?: string } | undefined;

        await postDevFrontDebug({
            user: user?.id || "No data",
            debugInfo,
        });

        setIsSendingState(false);
    };

    const { needRefresh, applyUpdate, checkUpdate, checkLoading, storeReset } =
        useSW();

    return (
        <Flex vertical gap={12}>
            <StyledInfoTitle>Налаштування</StyledInfoTitle>

            <StyledProfileCard>
                <Flex vertical gap={16}>
                    <StyledInfoBlock $borderBottom>
                        <StyledLinkAccount to={ROUTES.ProfileAccount}>
                            <StyledIcon as={UserOutlined} />

                            <Flex vertical gap="12px">
                                <StyledInfoTitle>Акаунт</StyledInfoTitle>

                                <SecondaryText>
                                    Керування даними: ім’я, E-mail, номер
                                    телефону, пароль
                                </SecondaryText>
                            </Flex>

                            <StyledIcon
                                as={RightOutlined}
                                style={{ margin: "auto" }}
                            />
                        </StyledLinkAccount>
                    </StyledInfoBlock>

                    <StyledInfoBlock $borderBottom gap={16}>
                        <StyledTwoColumnsGrid>
                            <Flex gap="12px" align="center">
                                <StyledIcon as={MoonOutlined} />
                                <StyledInfoTitle>Темна тема</StyledInfoTitle>
                            </Flex>

                            <StyledSwitch
                                checked={type === "dark"}
                                onChange={handleThemeChange}
                            />
                        </StyledTwoColumnsGrid>

                        <StyledTwoColumnsGrid>
                            <Flex gap="12px" align="center">
                                <StyledIcon as={DisconnectOutlined} />
                                <StyledInfoTitle>Офлайн режим</StyledInfoTitle>
                            </Flex>

                            <StyledSwitch
                                checked={offlineMode}
                                onChange={handleNetworkChange}
                                loading={offlineLoading}
                            />
                        </StyledTwoColumnsGrid>
                    </StyledInfoBlock>

                    <StyledInfoBlock gap={16}>
                        <StyledTwoColumnsGrid>
                            <Flex gap="12px" align="center">
                                <StyledIcon as={MailOutlined} />
                                <StyledInfoTitle>Стан додатку</StyledInfoTitle>
                            </Flex>
                            <Popconfirm
                                title="Відправити стан додатку?"
                                description={
                                    <>
                                        Ви впевнені, що хочете надіслати стан
                                        додатку на сервер?
                                        <br />
                                        Цю дію слід виконувати лише за наявності
                                        стабільного інтернет-з&apos;єднання.
                                    </>
                                }
                                onConfirm={handleSendState}
                                okText="Так"
                                cancelText="Ні"
                            >
                                <Button
                                    type="primary"
                                    loading={isSendingState}
                                    disabled={offlineMode}
                                >
                                    Надіслати
                                </Button>
                            </Popconfirm>
                        </StyledTwoColumnsGrid>

                        <Flex vertical gap={8}>
                            <StyledTwoColumnsGrid>
                                <Flex gap="12px" align="center">
                                    <StyledIcon as={SyncOutlined} />

                                    <StyledInfoTitle>Оновлення</StyledInfoTitle>
                                </Flex>

                                {needRefresh ? (
                                    <Popconfirm
                                        title={
                                            <Flex vertical gap={4}>
                                                <PrimaryText lineHeight="22px">
                                                    Ви впевнені що готові
                                                    оновити застосунок?
                                                </PrimaryText>

                                                {needRefresh && storeReset && (
                                                    <PrimaryText textColor="error">
                                                        {storeResetWarning}
                                                    </PrimaryText>
                                                )}
                                            </Flex>
                                        }
                                        onConfirm={applyUpdate}
                                        okText="Так"
                                        cancelText="Ні"
                                    >
                                        <Button
                                            disabled={offlineMode}
                                            type="primary"
                                        >
                                            Оновити
                                        </Button>
                                    </Popconfirm>
                                ) : (
                                    <Button
                                        onClick={() =>
                                            checkUpdate(notification)
                                        }
                                        loading={checkLoading}
                                        type="primary"
                                        disabled={offlineMode}
                                    >
                                        Перевірити
                                    </Button>
                                )}
                            </StyledTwoColumnsGrid>

                            {needRefresh && storeReset && (
                                <PrimaryText
                                    textColor="error"
                                    fontSize={16}
                                    fontWeight="500"
                                >
                                    {storeResetWarning}
                                </PrimaryText>
                            )}
                        </Flex>
                    </StyledInfoBlock>
                </Flex>
            </StyledProfileCard>
        </Flex>
    );
};
